import React, { useRef, useState } from 'react';
import { Button, Flag, Heading, Message, Tooltip } from '@lumx/react';
import { mdiAlertOutline, mdiApplicationOutline, mdiAccountMultipleOutline, mdiChevronDown, mdiClose, mdiLan, mdiEarth, mdiFilterVariant, mdiMagnify, mdiMapMarker, mdiInformationOutline, mdiLock } from '@mdi/js';
import MdiIcon from '@mdi/react';
import { useWizard } from '../context/WizardContext';
import UsersPage from './UsersPage';

type GroupType = 'Platform group' | 'Org unit group' | 'Location group';

interface Group {
  id: string;
  name: string;
  members: number;
  conflicts: number;
  site: string;
  orgUnit: string;
  orgUnitType: 'ou' | 'loc' | 'global';
  category: string;
  groupType: GroupType;
  locked: boolean; // used for role delegation — can't change before migration
}

const INITIAL_GROUPS: Group[] = [
  { id: 'g1',  name: 'Marketing Group',     members: 50,  conflicts: 0,   site: 'France',       orgUnit: 'Global',      orgUnitType: 'global', category: 'Business', groupType: 'Platform group',  locked: false },
  { id: 'g2',  name: 'Finance Group',        members: 50,  conflicts: 5,   site: 'Europe',       orgUnit: 'Europe',      orgUnitType: 'ou',     category: 'Business', groupType: 'Org unit group',  locked: false },
  { id: 'g3',  name: 'R&D Group',            members: 50,  conflicts: 5,   site: 'Switzerland',  orgUnit: 'Switzerland', orgUnitType: 'loc',    category: 'P&E',      groupType: 'Location group',  locked: false },
  { id: 'g4',  name: 'P&E Group',            members: 150, conflicts: 100, site: 'USA',          orgUnit: 'NorthAm',     orgUnitType: 'ou',     category: 'P&E',      groupType: 'Org unit group',  locked: true  },
  { id: 'g5',  name: 'Sales EMEA',           members: 200, conflicts: 0,   site: 'Germany',      orgUnit: 'EMEA',        orgUnitType: 'ou',     category: 'Sales',    groupType: 'Platform group',  locked: false },
  { id: 'g6',  name: 'HR Global',            members: 300, conflicts: 2,   site: 'Global',       orgUnit: 'Global',      orgUnitType: 'global', category: 'HR',       groupType: 'Platform group',  locked: true  },
  { id: 'g7',  name: 'Engineering EU',       members: 80,  conflicts: 8,   site: 'Netherlands',  orgUnit: 'EMEA',        orgUnitType: 'ou',     category: 'P&E',      groupType: 'Org unit group',  locked: false },
  { id: 'g8',  name: 'Legal Compliance',     members: 25,  conflicts: 0,   site: 'USA',          orgUnit: 'NorthAm',     orgUnitType: 'ou',     category: 'Business', groupType: 'Platform group',  locked: true  },
  { id: 'g9',  name: 'Customer Success',     members: 120, conflicts: 15,  site: 'Australia',    orgUnit: 'APAC',        orgUnitType: 'loc',    category: 'Business', groupType: 'Location group',  locked: false },
  { id: 'g10', name: 'Product Design',       members: 45,  conflicts: 3,   site: 'France',       orgUnit: 'EMEA',        orgUnitType: 'ou',     category: 'P&E',      groupType: 'Org unit group',  locked: false },
  { id: 'g11', name: 'IT Operations',        members: 60,  conflicts: 0,   site: 'UK',           orgUnit: 'EMEA',        orgUnitType: 'ou',     category: 'IT',       groupType: 'Platform group',  locked: true  },
  { id: 'g12', name: 'Data Science',         members: 35,  conflicts: 1,   site: 'Netherlands',  orgUnit: 'EMEA',        orgUnitType: 'ou',     category: 'P&E',      groupType: 'Org unit group',  locked: false },
  { id: 'g13', name: 'Finance APAC',         members: 40,  conflicts: 4,   site: 'Singapore',    orgUnit: 'APAC',        orgUnitType: 'loc',    category: 'Finance',  groupType: 'Location group',  locked: false },
  { id: 'g14', name: 'Comms & Brand',        members: 20,  conflicts: 0,   site: 'USA',          orgUnit: 'NorthAm',     orgUnitType: 'ou',     category: 'Business', groupType: 'Platform group',  locked: true  },
  { id: 'g15', name: 'Security',             members: 15,  conflicts: 0,   site: 'Global',       orgUnit: 'Global',      orgUnitType: 'global', category: 'IT',       groupType: 'Platform group',  locked: true  },
  { id: 'g16', name: 'Sales Americas',       members: 180, conflicts: 12,  site: 'Canada',       orgUnit: 'NorthAm',     orgUnitType: 'ou',     category: 'Sales',    groupType: 'Org unit group',  locked: false },
  { id: 'g17', name: 'Operations APAC',      members: 90,  conflicts: 7,   site: 'Japan',        orgUnit: 'APAC',        orgUnitType: 'loc',    category: 'P&E',      groupType: 'Location group',  locked: false },
  { id: 'g18', name: 'Executive Team',       members: 12,  conflicts: 0,   site: 'USA',          orgUnit: 'Global',      orgUnitType: 'global', category: 'Business', groupType: 'Platform group',  locked: true  },
];

const GROUP_TYPES: GroupType[] = ['Platform group', 'Org unit group', 'Location group'];

const OrgIcon: React.FC<{ type: 'ou' | 'loc' | 'global'; size?: number }> = ({ type, size = 0.7 }) => {
  const path = type === 'ou' ? mdiLan : type === 'loc' ? mdiMapMarker : mdiEarth;
  return <MdiIcon path={path} size={size} />;
};

const typeIcon = (t: GroupType) => {
  if (t === 'Platform group') return mdiEarth;
  if (t === 'Org unit group') return mdiLan;
  return mdiMapMarker;
};

const ORIGINAL_TYPES = Object.fromEntries(INITIAL_GROUPS.map(g => [g.id, g.groupType])) as Record<string, GroupType>;

interface Filters { site: string; orgUnit: string; category: string; groupType: string; }
const EMPTY_FILTERS: Filters = { site: '', orgUnit: '', category: '', groupType: '' };
const GROUP_TYPE_FILTER_OPTIONS = [
  'Platform group',
  'Org unit group',
  'Org unit group editable',
  'Location group',
  'Location group editable',
];

const SITE_OPTIONS   = [...new Set(INITIAL_GROUPS.map(g => g.site))].sort();
const ORGUNIT_OPTIONS = [...new Set(INITIAL_GROUPS.map(g => g.orgUnit))].sort();
const CATEGORY_OPTIONS = [...new Set(INITIAL_GROUPS.map(g => g.category))].sort();

const matchesGroupTypeFilter = (g: Group, ft: string): boolean => {
  if (!ft) return true;
  if (ft === 'Platform group')           return g.groupType === 'Platform group';
  if (ft === 'Org unit group')           return g.groupType === 'Org unit group' && g.locked;
  if (ft === 'Org unit group editable')  return g.groupType === 'Org unit group' && !g.locked;
  if (ft === 'Location group')           return g.groupType === 'Location group' && g.locked;
  if (ft === 'Location group editable')  return g.groupType === 'Location group' && !g.locked;
  return true;
};

const allowedTypes = (id: string): GroupType[] => {
  const orig = ORIGINAL_TYPES[id];
  if (orig === 'Platform group') return ['Platform group'];
  return [orig, 'Platform group'];
};

const GroupReconciliationPage: React.FC = () => {
  const { confirmGroups, sitesAssigned, sitesTotal } = useWizard();
  const sitesComplete = sitesTotal > 0 && sitesAssigned === sitesTotal;
  const [groups, setGroups] = useState<Group[]>(INITIAL_GROUPS);
  const [confirmed, setConfirmed] = useState(false);
  const [viewingGroupId, setViewingGroupId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [pendingFilters, setPendingFilters] = useState<Filters>(EMPTY_FILTERS);
  const [activeFilters, setActiveFilters] = useState<Filters>(EMPTY_FILTERS);
  const filterBtnRef = useRef<HTMLButtonElement>(null);

  const activeFilterCount = Object.values(activeFilters).filter(Boolean).length;

  const visibleGroups = groups.filter(g => {
    if (search && !g.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (activeFilters.site && g.site !== activeFilters.site) return false;
    if (activeFilters.orgUnit && g.orgUnit !== activeFilters.orgUnit) return false;
    if (activeFilters.category && g.category !== activeFilters.category) return false;
    if (!matchesGroupTypeFilter(g, activeFilters.groupType)) return false;
    return true;
  });

  const handleTypeChange = (id: string, newType: GroupType) => {
    setGroups(prev => prev.map(g => g.id === id ? { ...g, groupType: newType } : g));
  };

  const handleReset = () => {
    setGroups(INITIAL_GROUPS);
    setConfirmed(false);
  };

  const handleConfirm = () => {
    setConfirmed(true);
    confirmGroups();
  };

  const conflictWarning = (g: Group) => {
    if (g.groupType === 'Location group' && g.conflicts > 0) {
      return `${g.conflicts} members will be removed from the group`;
    }
    return null;
  };

  const viewingGroup = viewingGroupId ? groups.find(g => g.id === viewingGroupId) : null;
  if (viewingGroup) {
    return (
      <main className="main-content" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <UsersPage
          nodeId={viewingGroup.id}
          nodeName={viewingGroup.name}
          onBack={() => setViewingGroupId(null)}
        />
      </main>
    );
  }

  return (
    <div className="main-content" style={{ overflow: 'auto' }}>
      <div className="page-header">
        <div className="page-header-left" style={{ gap: 12 }}>
          <Heading as="h1" typography="headline">Group reconciliation</Heading>
          {confirmed
            ? <Flag color="green" label="GROUPS CONFIRMED" />
            : <Flag color="yellow" label="GROUPS NOT CONFIRMED" />
          }
        </div>
        <div className="page-header-right">
          <Button emphasis="medium" color="dark" onClick={handleReset}>Reset all</Button>
          <Tooltip
            label={!sitesComplete ? 'Complete site mapping before confirming groups' : null}
            placement="bottom"
          >
            <span>
              <Button
                emphasis="high"
                disabled={confirmed || !sitesComplete}
                onClick={handleConfirm}
              >
                Confirm
              </Button>
            </span>
          </Tooltip>
        </div>
      </div>

      <div style={{ padding: '16px 24px 0' }}>
        <Message kind="info" hasBackground>
          You can change group type only for groups that are currently not used for role delegation.
          You can change the group type of all other group types after the migration.
        </Message>
      </div>

      {/* Search + filter toolbar */}
      <div className="recon-toolbar">
        <div className="recon-search-wrap">
          <span className="recon-search-icon"><MdiIcon path={mdiMagnify} size={0.85} /></span>
          <input
            className="recon-search"
            placeholder="Search"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div style={{ position: 'relative' }}>
          <button
            ref={filterBtnRef}
            className={`recon-filter-btn${activeFilterCount > 0 ? ' recon-filter-btn--active' : ''}`}
            onClick={() => { setPendingFilters(activeFilters); setFilterOpen(v => !v); }}
          >
            <MdiIcon path={mdiFilterVariant} size={0.75} />
            Filters
            {activeFilterCount > 0 && <span className="recon-filter-badge">{activeFilterCount}</span>}
            <MdiIcon path={mdiChevronDown} size={0.7} />
          </button>

          {filterOpen && (
            <div className="recon-filter-panel">
              <div className="recon-filter-panel__header">
                <span className="recon-filter-panel__title">Filters</span>
                <button className="recon-icon-btn" onClick={() => setFilterOpen(false)}>
                  <MdiIcon path={mdiClose} size={0.7} />
                </button>
              </div>
              <div className="recon-filter-panel__body">
                {([
                  { label: 'Site', key: 'site', options: SITE_OPTIONS },
                  { label: 'Org Unit / Location', key: 'orgUnit', options: ORGUNIT_OPTIONS },
                  { label: 'Group category', key: 'category', options: CATEGORY_OPTIONS },
                  { label: 'Group type', key: 'groupType', options: GROUP_TYPE_FILTER_OPTIONS },
                ] as { label: string; key: keyof Filters; options: string[] }[]).map(({ label, key, options }) => (
                  <div key={key} className="recon-filter-field">
                    <label className="recon-filter-label">{label}</label>
                    <div className="recon-type-select-wrap" style={{ width: '100%' }}>
                      <select
                        className="recon-type-select recon-filter-select"
                        value={pendingFilters[key]}
                        onChange={e => setPendingFilters(prev => ({ ...prev, [key]: e.target.value }))}
                      >
                        <option value=""></option>
                        {options.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                      <span className="recon-type-select-chevron">
                        <MdiIcon path={mdiChevronDown} size={0.7} />
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="recon-filter-panel__footer">
                <button className="recon-filter-clear" onClick={() => { setPendingFilters(EMPTY_FILTERS); setActiveFilters(EMPTY_FILTERS); setFilterOpen(false); }}>
                  Clear all
                </button>
                <Button emphasis="high" onClick={() => { setActiveFilters(pendingFilters); setFilterOpen(false); }}>
                  Apply
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={{ padding: '0 0 32px', overflowX: 'auto' }}>
        <table className="recon-table">
          <thead>
            <tr className="recon-thead">
              <th>Name</th>
              <th>Members</th>
              <th>Site</th>
              <th>Org Unit / Location</th>
              <th>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  Group Category
                  <MdiIcon path={mdiInformationOutline} size={0.65} color="#4D4D4D" />
                </span>
              </th>
              <th>Group type</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visibleGroups.map(g => {
              const warning = conflictWarning(g);
              return (
                <tr key={g.id} className="recon-row">
                  {/* Name */}
                  <td>
                    <span className="recon-name">{g.name}</span>
                  </td>
                  {/* Members */}
                  <td style={{ fontSize: 13 }}>{g.members}</td>
                  {/* Site */}
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                      <span style={{ color: '#4D4D4D', display: 'flex' }}>
                        <MdiIcon path={mdiApplicationOutline} size={0.7} />
                      </span>
                      {g.site}
                    </div>
                  </td>
                  {/* Org Unit / Location */}
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                      <span style={{ color: '#4D4D4D', display: 'flex' }}>
                        <OrgIcon type={g.orgUnitType} />
                      </span>
                      {g.orgUnit}
                    </div>
                  </td>
                  {/* Group Category */}
                  <td style={{ fontSize: 13 }}>{g.category}</td>
                  {/* Group type */}
                  <td>
                    {g.locked ? (
                      <Tooltip
                        label="This group is used for role delegation. Type can not be changed before migration."
                        placement="top"
                      >
                        <div className={`recon-type-locked${!sitesComplete ? ' recon-type-locked--disabled' : ''}`}>
                          <MdiIcon path={typeIcon(g.groupType)} size={0.7} color={sitesComplete ? '#4D4D4D' : 'rgba(0,0,0,0.38)'} />
                          <span>{g.groupType}</span>
                          <MdiIcon path={mdiLock} size={0.6} color={sitesComplete ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.25)'} />
                        </div>
                      </Tooltip>
                    ) : (
                      <Tooltip
                        label={!sitesComplete ? 'You can change the group type after the site mapping process is concluded.' : null}
                        placement="top"
                      >
                        <div className={`recon-type-select-wrap${confirmed || !sitesComplete ? ' recon-type-select-wrap--disabled' : ''}`}>
                          <span className="recon-type-select-icon">
                            <MdiIcon path={typeIcon(g.groupType)} size={0.7} />
                          </span>
                          <select
                            className="recon-type-select"
                            value={g.groupType}
                            disabled={confirmed || !sitesComplete}
                            onChange={e => handleTypeChange(g.id, e.target.value as GroupType)}
                          >
                            {allowedTypes(g.id).map(t => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                          <span className="recon-type-select-chevron">
                            <MdiIcon path={mdiChevronDown} size={0.75} />
                          </span>
                        </div>
                      </Tooltip>
                    )}
                  </td>
                  <td>
                    <button
                      className="recon-icon-btn"
                      title="View users"
                      onClick={() => setViewingGroupId(g.id)}
                    >
                      <MdiIcon path={mdiAccountMultipleOutline} size={0.75} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GroupReconciliationPage;
